import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input } from '../../components/ui/FormField.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useRoleOptions } from '../../hooks/useRoleOptions.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { ApiError, api } from '../../services/api.js';
import {
  PASSWORD_INVALID_MESSAGE,
  PASSWORD_RULE_TEXT,
  satisfiesPasswordPolicy,
} from '../../utils/passwordPolicy.js';
import {
  RoleSupplierField,
  SUPPLIER_REQUIRED_MESSAGE,
  roleEffectLine,
  useSupplierChoices,
} from './RoleSupplierField.js';
import { CUSTOMER, RoleControls, SUPERADMIN } from './roleCard.js';
type CredentialMode = 'invite' | 'temp_password';
interface CreatedUser {
  user: { _id: string };
}

/** Admin create-user form (035 US1/US2). Role + credential mode (invite link or admin-set temporary password).
 * 367: the Role choice lists every role; an organisation role (Host, or a custom one) comes with its supplier. */
export function UserCreatePage() {
  const ability = useAbility();
  const canManage = ability.can('manage', 'User');
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // 140 FR-010c: must be a deliberate act, so it starts unticked. An administrator who simply leaves
  // the phone field blank is stopped rather than quietly allowed.
  const [phoneUnavailable, setPhoneUnavailable] = useState(false);
  // 376 (#1262): one staff role (none: customer only) and "Also a customer"; a new account starts as a customer.
  const [staffRole, setStaffRole] = useState<string>('');
  const [customer, setCustomer] = useState(true);
  // 334 (FR-003) and 367 (FR-001): every role, with whether this viewer may give it, from the platform.
  const { roleOptions } = useRoleOptions();
  const chosen = staffRole ? roleOptions.find((o) => o.name === staffRole) : undefined;
  const isOrganisationRole = chosen?.kind === 'organisation';
  const [credentialMode, setCredentialMode] = useState<CredentialMode>('invite');
  const [tempPassword, setTempPassword] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [error, setError] = useState('');
  const suppliers = useSupplierChoices(canManage && isOrganisationRole);

  // 161 (FR-003): creating a user invalidated nothing, so the Users list did not contain the new
  // account until the browser was reloaded.
  const create = useSaveMutation<Record<string, unknown>, CreatedUser>({
    resource: 'user',
    mutationFn: (body) => api.post<CreatedUser>('/users', body),
    onSaved: (res) => {
      setError('');
      navigate(`/users/${res.user._id}`);
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to create user',
      );
    },
  });

  if (!canManage) return null;

  function onSubmit() {
    if (!firstName || !lastName || !email) {
      setError('First name, last name and email are required.');
      return;
    }
    if (
      credentialMode === 'temp_password' &&
      tempPassword &&
      !satisfiesPasswordPolicy(tempPassword)
    ) {
      // 140 US3: this path accepted any eight characters before — a temporary password the
      // change-password screen would have refused, which the user then had to change on first sign-in
      // anyway. Now the same rule everywhere.
      setError(PASSWORD_INVALID_MESSAGE);
      return;
    }
    if (credentialMode === 'temp_password' && !tempPassword) {
      setError('Enter a temporary password.');
      return;
    }
    if (!phone && !phoneUnavailable) {
      setError('A phone number is required, or mark it unavailable');
      return;
    }
    const alsoCustomer = staffRole !== SUPERADMIN && customer;
    if (!staffRole && !alsoCustomer) {
      setError('Choose a staff role, or tick Also a customer.');
      return;
    }
    if (isOrganisationRole && !supplierId) {
      setError(SUPPLIER_REQUIRED_MESSAGE);
      return;
    }
    const body: Record<string, unknown> = {
      first_name: firstName,
      last_name: lastName,
      email,
      // 376: the staff role, or Customer for a customer-only account; `customer` adds Customer to a staff role.
      role: staffRole || CUSTOMER,
      credential_mode: credentialMode,
    };
    if (staffRole && alsoCustomer) body.customer = true;
    if (phone) body.phone = phone;
    // 140 FR-010b: transmitted, never stored. The boundary requires a number OR this, so the rule
    // holds for every caller — a checkbox alone would have left the API accepting an omission from
    // anyone, which is the browser-deep validation this feature exists to remove.
    if (phoneUnavailable) body.phone_unavailable = true;
    if (credentialMode === 'temp_password') body.temp_password = tempPassword;
    if (isOrganisationRole) body.supplier_id = supplierId;
    void create.save(body);
  }

  const effect = roleEffectLine({
    personName: [firstName, lastName].filter(Boolean).join(' ') || 'This person',
    role: chosen,
    supplierName: suppliers.find((s) => s._id === supplierId)?.company_name ?? null,
  });

  return (
    <div className="max-w-2xl">
      <PageHeader title="Create user" fallback="/users" />

      <Card className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="First name" name="first_name" required>
            <Input
              id="first_name"
              name="first_name"
              value={firstName}
              onChange={(e) => setFirstName(e.currentTarget.value)}
            />
          </FormField>
          <FormField label="Last name" name="last_name" required>
            <Input
              id="last_name"
              name="last_name"
              value={lastName}
              onChange={(e) => setLastName(e.currentTarget.value)}
            />
          </FormField>
        </div>

        <FormField label="Email" name="email" required>
          <Input
            id="email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
        </FormField>

        <FormField
          label="Phone"
          name="phone"
          required={!phoneUnavailable}
          hint={phoneUnavailable ? 'Marked unavailable' : undefined}
        >
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={phone}
            disabled={phoneUnavailable}
            onChange={(e) => setPhone(e.currentTarget.value)}
          />
        </FormField>
        <label htmlFor="phone_unavailable" className="flex items-center gap-2 text-sm text-steel">
          <input
            id="phone_unavailable"
            name="phone_unavailable"
            type="checkbox"
            checked={phoneUnavailable}
            onChange={(e) => {
              const next = e.currentTarget.checked;
              setPhoneUnavailable(next);
              // Clearing the field when the box is ticked keeps the two from being sent together,
              // which the boundary refuses as contradictory.
              if (next) setPhone('');
            }}
          />
          No phone number available
        </label>

        <div className="grid gap-2">
          <h3 className="text-sm font-semibold text-ink">Role</h3>
          <RoleControls
            idPrefix="create"
            staffRole={staffRole}
            customer={customer}
            onStaffRole={setStaffRole}
            onCustomer={setCustomer}
            options={roleOptions}
          />
        </div>
        {isOrganisationRole && (
          <RoleSupplierField
            id="role_supplier"
            roleName={staffRole}
            value={supplierId}
            onChange={setSupplierId}
            suppliers={suppliers}
          />
        )}
        {effect && (
          <p
            data-testid="role-effect"
            className="-mt-2 rounded-md bg-brand-blue-soft px-3 py-2 text-sm text-ink"
          >
            {effect}
          </p>
        )}

        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium text-ink mb-1">Credentials</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="credential_mode"
              value="invite"
              checked={credentialMode === 'invite'}
              onChange={() => setCredentialMode('invite')}
            />
            Send an invite email (the user sets their own password)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="credential_mode"
              value="temp_password"
              checked={credentialMode === 'temp_password'}
              onChange={() => setCredentialMode('temp_password')}
            />
            Set a temporary password (the user must change it on first sign-in)
          </label>
        </fieldset>

        {credentialMode === 'temp_password' && (
          <FormField
            label="Temporary password"
            name="temp_password"
            required
            hint={PASSWORD_RULE_TEXT}
          >
            <Input
              id="temp_password"
              name="temp_password"
              type="text"
              value={tempPassword}
              onChange={(e) => setTempPassword(e.currentTarget.value)}
            />
          </FormField>
        )}

        <div>
          <Button loading={create.isPending} onClick={onSubmit}>
            Create user
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </Card>
    </div>
  );
}
